import { useEffect, useState } from "react";
import { useAuth } from "@/lib/auth";
import { PageHeader, Field } from "@/components/UI";
import { User, KeyRound, CheckCircle2, Eye, EyeOff, Loader2, Phone, Bell } from "lucide-react";
import { api } from "@/lib/api";
import { useToast } from "@/lib/toast";

export default function Profile() {
  const { user } = useAuth();
  if (!user) return null;

  return (
    <div data-testid="page-profile" className="max-w-2xl space-y-5">
      <PageHeader
        icon={User}
        title="My profile"
        subtitle="Personal account information & security"
      />

      <div className="card card-padded">
        <div className="flex items-center gap-4 mb-6">
          <div className="avatar avatar-lg">
            {(user.full_name || user.email).charAt(0).toUpperCase()}
          </div>
          <div>
            <div className="font-bold text-lg">{user.full_name || "—"}</div>
            <div className="text-sm muted">{user.email}</div>
          </div>
        </div>

        <div className="divide-y divide-slate-100">
          {[
            ["Role", user.role.toUpperCase()],
            ["Status", user.is_active ? "Active" : "Inactive"],
            ["Member since", new Date(user.created_at).toLocaleDateString()],
            ["Last login", user.last_login ? new Date(user.last_login).toLocaleString() : "—"],
          ].map(([k, v]) => (
            <div key={k} className="py-3 flex items-center justify-between">
              <span className="text-sm muted">{k}</span>
              <span className="text-sm font-semibold">{v}</span>
            </div>
          ))}
        </div>
      </div>

      <ContactCard />
      <NotificationPrefsCard />
      <ChangePasswordCard />
    </div>
  );
}

function ContactCard() {
  const { user, refreshMe } = useAuth();
  const { toast } = useToast();
  const [phone, setPhone] = useState(user.phone || "");
  const [saving, setSaving] = useState(false);
  const dirty = (phone || "") !== (user.phone || "");

  const save = async () => {
    setSaving(true);
    try {
      await api.updateMe({ phone });
      await refreshMe();
      toast("Phone updated", "success");
    } catch (e) {
      toast(e?.response?.data?.detail || "Update failed", "error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="card card-padded" data-testid="contact-card">
      <div className="flex items-center gap-2 mb-4">
        <Phone size={16} style={{ color: "var(--green-700)" }} />
        <div>
          <div className="font-bold">Contact preferences</div>
          <div className="text-xs muted">
            Used to send ticket updates via email and (soon) WhatsApp.
          </div>
        </div>
      </div>
      <Field label="Phone number">
        <input
          data-testid="profile-phone"
          type="tel"
          className="input"
          placeholder="+256 7xx xxx xxx"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
        />
      </Field>
      <div className="flex justify-end mt-3">
        <button
          data-testid="save-phone"
          disabled={!dirty || saving}
          onClick={save}
          className="btn btn-primary btn-sm"
        >
          {saving ? <Loader2 size={12} className="animate-spin" /> : <CheckCircle2 size={12} />}
          Save phone
        </button>
      </div>
    </div>
  );
}

function NotificationPrefsCard() {
  const { user, refreshMe } = useAuth();
  const { toast } = useToast();
  const [schema, setSchema] = useState({ channels: [], events: [] });
  const [prefs, setPrefs] = useState(user.notification_prefs || {});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.notificationsSchema().then(setSchema).catch(() => {});
  }, []);

  useEffect(() => {
    setPrefs(user.notification_prefs || {});
  }, [user.notification_prefs]);

  const get = (kind, key) => {
    const v = (prefs?.[kind] || {})[key];
    return v !== false; // default ON
  };
  const set = (kind, key, val) => {
    setPrefs((p) => ({
      ...p,
      [kind]: { ...(p?.[kind] || {}), [key]: val },
    }));
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.updateMe({ notification_prefs: prefs });
      try { await refreshMe(); } catch { /* ignore */ }
      toast("Notification preferences saved", "success");
    } catch (e) {
      toast(e?.response?.data?.detail || "Save failed", "error");
    } finally {
      setSaving(false);
    }
  };

  const setAll = (val) => {
    const next = { channels: {}, events: {} };
    for (const c of schema.channels) next.channels[c.key] = val;
    for (const e of schema.events) next.events[e.key] = val;
    setPrefs(next);
  };

  return (
    <div className="card card-padded" data-testid="notif-prefs-card">
      <div className="flex items-start justify-between gap-4 mb-4">
        <div className="flex items-center gap-2">
          <Bell size={16} style={{ color: "var(--green-700)" }} />
          <div>
            <div className="font-bold">Notification preferences</div>
            <div className="text-xs muted">
              Choose which channels and ticket events you want to receive updates on.
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button
            data-testid="notif-all-on"
            onClick={() => setAll(true)}
            className="btn btn-ghost btn-sm text-[11px]"
          >
            All on
          </button>
          <button
            data-testid="notif-all-off"
            onClick={() => setAll(false)}
            className="btn btn-ghost btn-sm text-[11px]"
          >
            All off
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <div>
          <div className="text-[11px] uppercase font-semibold muted mb-2">Channels</div>
          <div className="space-y-2">
            {schema.channels.map((c) => (
              <Toggle
                key={c.key}
                testId={`ch-${c.key}`}
                label={c.label}
                checked={get("channels", c.key)}
                onChange={(v) => set("channels", c.key, v)}
              />
            ))}
          </div>
        </div>
        <div>
          <div className="text-[11px] uppercase font-semibold muted mb-2">Events</div>
          <div className="space-y-2">
            {schema.events.map((e) => (
              <Toggle
                key={e.key}
                testId={`ev-${e.key}`}
                label={e.label}
                checked={get("events", e.key)}
                onChange={(v) => set("events", e.key, v)}
              />
            ))}
          </div>
        </div>
      </div>

      <div className="flex justify-end mt-4 pt-3 border-t border-slate-100">
        <button
          data-testid="save-notif-prefs"
          disabled={saving}
          onClick={save}
          className="btn btn-primary btn-sm"
        >
          {saving ? <Loader2 size={12} className="animate-spin" /> : <CheckCircle2 size={12} />}
          Save preferences
        </button>
      </div>
    </div>
  );
}

function Toggle({ checked, onChange, label, testId }) {
  return (
    <label
      data-testid={testId}
      className="flex items-center justify-between gap-3 cursor-pointer text-sm"
    >
      <span style={{ color: checked ? "var(--ink-900)" : "var(--ink-500)" }}>{label}</span>
      <button
        type="button"
        onClick={() => onChange(!checked)}
        role="switch"
        aria-checked={checked}
        className="relative inline-flex items-center h-5 w-9 rounded-full transition-colors"
        style={{ background: checked ? "var(--green-700)" : "#cbd5e1" }}
      >
        <span
          className="inline-block h-4 w-4 rounded-full bg-white shadow transform transition-transform"
          style={{ transform: `translateX(${checked ? 18 : 2}px)` }}
        />
      </button>
    </label>
  );
}


function ChangePasswordCard() {
  const { toast } = useToast();
  const { refreshMe } = useAuth();
  const [form, setForm] = useState({ current_password: "", new_password: "", confirm: "" });
  const [show, setShow] = useState({ current: false, next: false });
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.current_password || !form.new_password) {
      toast("Fill both password fields", "error");
      return;
    }
    if (form.new_password.length < 8) {
      toast("New password must be at least 8 characters", "error");
      return;
    }
    if (form.new_password !== form.confirm) {
      toast("New password and confirmation don't match", "error");
      return;
    }
    setSaving(true);
    try {
      await api.changePassword(form.current_password, form.new_password);
      toast("Password updated successfully", "success");
      setForm({ current_password: "", new_password: "", confirm: "" });
      // Refresh /auth/me so must_change_password flag clears
      try { await refreshMe(); } catch { /* ignore refresh errors */ }
    } catch (e) {
      toast(e?.response?.data?.detail || "Password update failed", "error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="card card-padded" data-testid="change-password-card">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 rounded-xl flex items-center justify-center"
             style={{ background: "var(--green-50)", color: "var(--green-700)" }}>
          <KeyRound size={16} />
        </div>
        <div>
          <div className="font-bold text-base">Change password</div>
          <div className="text-xs muted">Choose a strong password — at least 8 characters</div>
        </div>
      </div>

      <form onSubmit={submit} className="space-y-3">
        <Field label="Current password">
          <div className="relative">
            <input
              data-testid="pwd-current"
              type={show.current ? "text" : "password"}
              className="input pr-10"
              value={form.current_password}
              onChange={(e) => setForm({ ...form, current_password: e.target.value })}
              autoComplete="current-password"
            />
            <button
              type="button"
              onClick={() => setShow({ ...show, current: !show.current })}
              className="absolute right-3 top-3 text-slate-400 hover:text-slate-700"
              tabIndex={-1}
            >
              {show.current ? <EyeOff size={14} /> : <Eye size={14} />}
            </button>
          </div>
        </Field>
        <Field label="New password">
          <div className="relative">
            <input
              data-testid="pwd-new"
              type={show.next ? "text" : "password"}
              className="input pr-10"
              value={form.new_password}
              onChange={(e) => setForm({ ...form, new_password: e.target.value })}
              autoComplete="new-password"
              minLength={8}
            />
            <button
              type="button"
              onClick={() => setShow({ ...show, next: !show.next })}
              className="absolute right-3 top-3 text-slate-400 hover:text-slate-700"
              tabIndex={-1}
            >
              {show.next ? <EyeOff size={14} /> : <Eye size={14} />}
            </button>
          </div>
        </Field>
        <Field label="Confirm new password">
          <input
            data-testid="pwd-confirm"
            type={show.next ? "text" : "password"}
            className="input"
            value={form.confirm}
            onChange={(e) => setForm({ ...form, confirm: e.target.value })}
            autoComplete="new-password"
            minLength={8}
          />
        </Field>
        <div className="pt-2 flex items-center justify-end gap-2">
          {form.new_password && form.new_password === form.confirm && form.new_password.length >= 8 && (
            <span className="text-xs flex items-center gap-1" style={{ color: "var(--green-700)" }}>
              <CheckCircle2 size={12} /> Passwords match
            </span>
          )}
          <button
            data-testid="pwd-submit"
            type="submit"
            disabled={saving}
            className="btn btn-primary"
          >
            {saving ? <Loader2 size={14} className="animate-spin" /> : <KeyRound size={14} />}
            Update password
          </button>
        </div>
      </form>
    </div>
  );
}
